import { getIn } from 'formik';
import { round, sumBy, range, cloneDeep } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import styled from 'styled-components/macro';
import { useTemporaryBoolean } from '../../../../../helpers/hooks/useTemporaryBoolean';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { ReactComponent as ErrorIcon } from '../../../../../images/icons/error-circle-outline.svg';
import { FieldErrorIndicator } from '../../../../../shared/form/FieldErrorIndicator';
import { FormContext } from '../../../../../shared/form/Form';
import { TextInputField } from '../../../../../shared/form/inputs/TextInputField';
import { colourPurple09, colourPurple10, colourRed06 } from '../../../../../styling/colours';
import { fontWeightBold } from '../../../../../styling/fonts';
import { spacing02, spacing03, spacing04, spacing14 } from '../../../../../styling/spacing';
import { DeleteAndDuplicateButtons, DuplicatedSuccessMessage } from '../../DeleteAndDuplicateButtons';
import { DeleteConfirmation } from '../../DeleteConfirmation';
import { DragHandle } from '../../drag-and-drop/DragHandle';
import { QuoteOrDesignFormContext } from '../../QuoteOrDesignFormContext';
import { QuoteOrDesignFormExpandControlChevron } from '../../QuoteOrDesignFormExpandControlChevron';
import { manifoldFieldName, roomFieldName, unitFieldName } from '../../quoteOrDesignFormFieldNameHelpers';
import { RoomFormModel } from '../../rooms/roomFormModel';
import { SectionHeader } from '../../SectionHeader';
import { UnitFieldContext } from '../../units/UnitFieldContext';
import { DesignFormUnitViewType, UnitFormModel } from '../../units/unitFormModel';
import { ManifoldFormModel } from '../manifoldFormModel';
import { DesignFormUnitViewTypeButtons } from './DesignFormUnitViewTypeButtons';
import { FillDownRoomsButton } from './FillDownRoomsButton';

type Props = {
  unitIndex: number;
  unit: UnitFormModel;
  manifoldIndex: number;
  manifold: ManifoldFormModel;
  isExpanded: boolean;
  dragHandleProps: DraggableProvidedDragHandleProps | null;
  deleteManifold: () => void;
  duplicateManifold: () => void;
};

export const ManifoldFieldsHeader = ({
  unit,
  manifoldIndex,
  manifold,
  isExpanded,
  dragHandleProps,
  deleteManifold,
  duplicateManifold,
}: Props) => {
  const formContext = assertNotNull(useContext(QuoteOrDesignFormContext));
  const { formType, formProps } = formContext;

  const unitFieldContext = assertNotNull(useContext(UnitFieldContext));
  const { unitIndex } = unitFieldContext;

  const { setFieldValue, errors, touched } = formProps;

  const isInReadOnlyMode = useContext(FormContext).isDisabled;

  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const [shouldShowDuplicatedSuccessMessage, showDuplicatedSuccessMessage] = useTemporaryBoolean();

  const onClickDuplicate = () => {
    duplicateManifold();
    showDuplicatedSuccessMessage();
  };

  const fillDownRoomFields = (fieldName: keyof RoomFormModel) => {
    if (manifold.rooms == null || manifold.rooms.length === 0) {
      return;
    }

    const numberOfRooms = manifold.rooms.length;
    const valueToFill = cloneDeep(manifold.rooms[0][fieldName]);

    range(1, numberOfRooms).map(roomIndex =>
      setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, fieldName), valueToFill),
    );
  };

  const manifoldHasRooms = manifold.rooms != null ? manifold.rooms.length > 0 : false;

  let totalUnderfloorHeatingAreaForManifoldInMetresSquared = 0;
  let totalNumberOfPorts = 0;
  if (manifoldHasRooms && !manifold.isRadiator) {
    totalUnderfloorHeatingAreaForManifoldInMetresSquared = sumBy(
      assertNotNull(manifold.rooms).filter(
        room =>
          room.floorConstruction == null ||
          !(room.floorConstruction.isElectricUnderfloorHeating || room.floorConstruction.isSkirtingHeating),
      ),
      room => Number(room.areaInMetresSquared),
    );
    totalNumberOfPorts = sumBy(manifold.rooms, room => Number(room.numberOfPorts));
  } else if (manifold.roomlessManifold != null) {
    totalUnderfloorHeatingAreaForManifoldInMetresSquared =
      manifold.roomlessManifold.floorConstruction != null &&
      manifold.roomlessManifold.floorConstruction.isElectricUnderfloorHeating
        ? 0
        : Number(manifold.roomlessManifold.areaInMetresSquared);
    totalNumberOfPorts = Number(manifold.roomlessManifold.numberOfPorts);
  }

  useEffect(() => {
    if (!isNaN(totalNumberOfPorts)) {
      setFieldValue(manifoldFieldName(unitIndex, manifoldIndex, 'totalNumberOfPorts'), `${totalNumberOfPorts}`);
    }
  }, [totalNumberOfPorts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNaN(totalUnderfloorHeatingAreaForManifoldInMetresSquared)) {
      setFieldValue(
        manifoldFieldName(unitIndex, manifoldIndex, 'totalUnderfloorHeatingAreaInMetresSquared'),
        `${round(totalUnderfloorHeatingAreaForManifoldInMetresSquared, 1)}`,
      );
    }
  }, [totalUnderfloorHeatingAreaForManifoldInMetresSquared]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPortsError = getIn(errors, manifoldFieldName(unitIndex, manifoldIndex, 'totalNumberOfPorts'));
  const skirtingHeatingError = getIn(errors, manifoldFieldName(unitIndex, manifoldIndex, 'skirtingHeatingValidation'));

  const manifoldBaseFieldName = `units.${unitIndex}.manifolds.${manifoldIndex}`;
  const hasNestedError = getIn(errors, manifoldBaseFieldName) && getIn(touched, manifoldBaseFieldName);

  const setDesignFormViewType = (viewType: DesignFormUnitViewType) => {
    setFieldValue(unitFieldName(unitIndex, 'designViewType'), viewType);
  };

  return (
    <>
      <ManifoldSectionHeader>
        <SectionHeaderLeft>
          <DragHandle dragHandleProps={dragHandleProps} disabled={isInReadOnlyMode} />
          {hasNestedError && (
            <NestedErrorsIndicator
              fieldName={`units.${unitIndex}.manifolds.${manifoldIndex}`}
              showError={hasNestedError}
              error="Some fields within this Manifold are invalid"
            />
          )}
          <VariableWidthTextInputField
            name={manifoldFieldName(unitIndex, manifoldIndex, 'name')}
            label={null}
            $width={formType === 'Quote' ? spacing14 : null}
          />
          <QuoteOrDesignFormExpandControlChevron
            onClick={() => {
              setFieldValue(manifoldFieldName(unitIndex, manifoldIndex, 'isExpanded'), !isExpanded);
            }}
            isExpanded={isExpanded}
            testId={manifoldExpandControlChevronTestId(unitIndex, manifoldIndex)}
          />
          {!isInReadOnlyMode && (
            <>
              <DeleteAndDuplicateButtons
                onClickDelete={() => setIsConfirmingDelete(true)}
                onClickDuplicate={onClickDuplicate}
                deleteButtonTestId={manifoldDeleteButtonTestId(unitIndex, manifoldIndex)}
              />
              {manifold.rooms != null && manifold.rooms.length > 0 && (
                <FillDownRoomsButton fillDownRoomFields={fillDownRoomFields} />
              )}
              <DuplicatedSuccessMessage $visible={shouldShowDuplicatedSuccessMessage}>
                Duplicated
              </DuplicatedSuccessMessage>
            </>
          )}
        </SectionHeaderLeft>
        {formType === 'Design' && (
          <SectionHeaderRight>
            <DesignFormUnitViewTypeButtons
              unitIndex={unitIndex}
              manifoldIndex={manifoldIndex}
              selectedViewType={unit.designViewType}
              setViewType={setDesignFormViewType}
            />
          </SectionHeaderRight>
        )}
      </ManifoldSectionHeader>
      <DeleteConfirmation
        isConfirmingDelete={isConfirmingDelete}
        setIsConfirmingDelete={setIsConfirmingDelete}
        deleteItem={deleteManifold}
        deleteButtonTestId={manifoldConfirmDeleteButtonTestId(unitIndex, manifoldIndex)}
      />
      {manifoldHasRooms && !manifold.isRadiator && (
        <ManifoldSummary>
          <span>
            {manifold.totalUnderfloorHeatingAreaInMetresSquared} m<sup>2</sup> of underfloor heating with{' '}
            {manifold.totalNumberOfPorts} port{totalNumberOfPorts !== 1 && 's'}
          </span>
          {totalPortsError && (
            <ManifoldError>
              <ErrorIcon />
              {totalPortsError}
            </ManifoldError>
          )}
          {skirtingHeatingError && (
            <ManifoldError>
              <ErrorIcon />
              {skirtingHeatingError}
            </ManifoldError>
          )}
        </ManifoldSummary>
      )}
    </>
  );
};

const manifoldExpandControlChevronTestId = (unitIndex: number, manifoldIndex: number) =>
  `unit-${unitIndex}-manifold-${manifoldIndex}-expand-control-chevron`;

export const manifoldDeleteButtonTestId = (unitIndex: number, manifoldIndex: number) =>
  `unit-${unitIndex}-manifold-${manifoldIndex}-delete-button`;

export const manifoldConfirmDeleteButtonTestId = (unitIndex: number, manifoldIndex: number) =>
  `unit-${unitIndex}-manifold-${manifoldIndex}-confirm-delete-button`;

export const NestedErrorsIndicator = styled(FieldErrorIndicator)`
  margin-right: ${spacing03};
`;

export const ManifoldSummary = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing04};
  font-weight: ${fontWeightBold};
  height: 34px;
  padding: ${spacing02} ${spacing04};
  background-color: ${colourPurple10};
  border: solid 2px ${colourPurple09};
`;

export const ManifoldError = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${colourRed06};
  margin-left: ${spacing03};
  height: 34px;

  svg {
    height: 22px;
    width: 22px;
    margin-right: ${spacing02};
  }
`;

const ManifoldSectionHeader = styled(SectionHeader)`
  justify-content: space-between;
`;

const SectionHeaderLeft = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

const VariableWidthTextInputField = styled(TextInputField)<{ $width: string | null }>`
  width: ${props => props.$width};
`;

const SectionHeaderRight = styled.div``;
